import { call, put, select, takeEvery } from 'redux-saga/effects';
import { RequestStatus, SHOW_NOTIFY, NOTIFY_LEVEL } from 'constants';
import { Utils } from 'mw-style-react';
import {
  SET_ACTIVE_ELEMENT,
  BULK_REMOVE_FROM_GRAPH,
  BULK_ADD_TO_GRAPH,
  COPY_FROM_GRAPH,
  PASTE_TO_GRAPH,
} from '@control-front-end/common/constants/graphActors';
import api from '@control-front-end/common/sagas/api';
import { getGraphEls, sendReducerMsg, getActiveLayer } from './graphHelpers';
import { resubscribeBalances } from './graphRealtime';
import {
  removeGroupTplActors,
  addAllDescendants,
  createNewEdges,
  getGraphElsAfterPaste,
} from './graphCopyHelpers';
import { manageLayerElements } from '../../routes/ActorsGraph/sagas/layers/layerElements';

const _COPY_GRAPH_KEY_ = 'actors_graph_clipboard';

/**
 * Скопировать элементы слоя графа
 */
function* copyGraphElements({ payload, callback }) {
  const { hasLoops } = payload;
  const { active: accId } = yield select((state) => state.accounts);
  for (const n of payload.nodes) {
    if (!n.isMore || !n.collapsed) continue;
    // для свернутых more-узлов копируем все дочерние
    yield addAllDescendants(n, payload);
  }
  const { nodes, edges } = yield removeGroupTplActors(payload);
  const copyNodes = JSON.parse(JSON.stringify(nodes));
  copyNodes.forEach((el) => {
    el.id = el.actorId;
    el.prevId = el.laId;
    delete el.laId;
  });
  const copiedEdges = JSON.parse(JSON.stringify(edges));
  // исключаем связи, для которых не скопирован source и(или) target актор
  const copyEdges = copiedEdges.filter((edge) => {
    const sourceNode = copyNodes.find((n) => n.actorId === edge.sourceActorId);
    const targetNode = copyNodes.find((n) => n.actorId === edge.targetActorId);
    return sourceNode && targetNode;
  });
  copyEdges.forEach((el) => {
    el.id = el.edgeId;
    el.source = el.sourceActorId;
    el.target = el.targetActorId;
    el.prevId = el.laId;
    el.prevSource = el.laIdSource;
    el.prevTarget = el.laIdTarget;
    delete el.laId;
    delete el.laIdSource;
    delete el.laIdTarget;
  });
  Utils.toStorage(
    _COPY_GRAPH_KEY_,
    JSON.stringify({
      accId,
      nodes: copyNodes,
      edges: copyEdges,
      hasLoops,
    })
  );
  yield put({ type: COPY_FROM_GRAPH.SUCCESS });
  if (callback) callback();
}

/**
 * Получить скопированые элементы из sessionStorage
 */
function getCopiedGraphElements({ position }) {
  const copiedEls = Utils.fromStorage(_COPY_GRAPH_KEY_);
  if (!copiedEls) return null;
  const { accId, nodes, edges, hasLoops } = JSON.parse(copiedEls);
  if (!nodes || !nodes.length) return null;
  const offsetX = position.x - nodes[0].position.x;
  const offsetY = position.y - nodes[0].position.y;
  nodes.forEach((el) => {
    if (el.position) {
      el.position = {
        x: el.position.x + offsetX,
        y: el.position.y + offsetY,
      };
    }
  });
  return { accId, nodes, edges, hasLoops };
}

/**
 * Вставить скопированные элементы слоя графа
 */
function* pasteGraphElements({ payload, callback }) {
  const { layerId, typeLayer, type, position } = payload;
  const copiedEls = yield call(getCopiedGraphElements, { position });
  if (!copiedEls) return;
  const { active: accId } = yield select((state) => state.accounts);
  const { nodes, edges, accId: accIdPrev, hasLoops } = copiedEls;
  if (type === 'tree' && hasLoops) {
    yield put({
      type: SHOW_NOTIFY.REQUEST,
      payload: {
        id: 'loopsError',
        type: NOTIFY_LEVEL.ERROR,
        label: 'Unable to paste actors with a loop on a tree layer',
      },
    });
    return;
  }
  if (accId !== accIdPrev) {
    yield put({
      type: SHOW_NOTIFY.REQUEST,
      payload: {
        id: 'pasteActorsError',
        type: NOTIFY_LEVEL.ERROR,
        label: 'Unable to paste actors from another workspace.',
      },
    });
    return;
  }
  const edgeTypes = yield select((state) => state.edgeTypes);
  const edgeTypeH = edgeTypes.find((i) => i.name === 'hierarchy');
  const edgeTypeId =
    typeLayer === 'trees' ? layerId.split('_')[1] : edgeTypeH.id;
  // признак, подходят ли скопированные связи по edgeTypeId для слоя
  const sameEdgeType = edges.length && edges[0].edgeTypeId === edgeTypeId;
  const layerEdges = [];
  if (typeLayer === 'layers') {
    const layerNodes = nodes.map((node) => ({
      action: 'create',
      data: {
        id: node.actorId,
        type: 'node',
        model: node,
        position: node.position,
      },
    }));
    // добавляем на слой акторы и мапим в модель новые laId
    const { nodesMap } = yield call(manageLayerElements, {
      payload: {
        layerId,
        body: layerNodes,
        withReq: true,
        subscribeBalances: false,
        reduxEvent: false,
      },
    });
    nodes.forEach((el) => {
      const layerNode = nodesMap.find(
        (i) => i.actorId === el.actorId && !nodes.find((n) => n.laId === i.laId)
      );
      el.laId = layerNode.laId;
    });
    // проставляем в связях laId для source и target
    edges.forEach((el) => {
      const laSource = nodes.find(
        (i) => i.prevId === el.prevSource && i.id === el.sourceActorId
      );
      const laTarget = nodes.find(
        (i) => i.prevId === el.prevTarget && i.id === el.targetActorId
      );
      layerEdges.push({
        action: 'create',
        data: {
          id: el.edgeId,
          type: 'edge',
          model: el,
          laIdSource: laSource.laId,
          laIdTarget: laTarget.laId,
        },
      });
      el.laIdSource = laSource.laId;
      el.laIdTarget = laTarget.laId;
    });
    // если edgeType соответствует, то добавляем на слой связи и мапим в модель новые laId
    if (layerEdges.length && sameEdgeType) {
      const { edgesMap } = yield call(manageLayerElements, {
        payload: {
          layerId,
          body: layerEdges,
          withReq: true,
          subscribeBalances: false,
          reduxEvent: false,
        },
      });
      edges.forEach((el, index) => {
        el.laId = edgesMap[index].laId;
      });
    }
  }
  const newGraphEls = yield call(getGraphElsAfterPaste, {
    nodes,
    edges,
    typeLayer,
    sameEdgeType,
    isTree: type === 'tree',
  });
  yield sendReducerMsg({
    type: PASTE_TO_GRAPH.SUCCESS,
    payload: newGraphEls,
  });
  // создать новые связи с другим edgeTypeId
  if (edges.length && !sameEdgeType) {
    yield createNewEdges(edges, edgeTypeId);
  }
  yield resubscribeBalances({ layerId });
  Utils.delSessionStorage(_COPY_GRAPH_KEY_);
  if (callback) callback(nodes.concat(edges));
}

/**
 * Нанести элементы на слой графа
 */
function* bulkAddEls({ payload, callback }) {
  const { layerId } = payload;
  const { nodes, edges } = yield removeGroupTplActors(payload);
  // Добавить акторы на слой
  const layerNodes = [];
  nodes.forEach((el) => {
    const data = { id: el.actorId, model: el, type: 'node' };
    if (el.position) data.position = el.position;
    layerNodes.push({ action: 'create', data });
  });
  const { nodesMap } = yield call(manageLayerElements, {
    payload: {
      layerId,
      body: layerNodes,
      withReq: true,
      subscribeBalances: false,
    },
  });
  nodesMap.forEach((i, index) => {
    const node = nodes[index];
    i.prevId = node.id;
  });
  // Добавить связи на слой
  const layerEdges = [];
  edges.forEach((el) => {
    const { source, target } = el;
    const laSource = nodesMap.find((i) => i.prevId === source);
    const laTarget = nodesMap.find((i) => i.prevId === target);
    layerEdges.push({
      action: 'create',
      data: {
        id: el.edgeId,
        type: 'edge',
        laIdSource: laSource.laId,
        laIdTarget: laTarget.laId,
      },
    });
  });
  if (layerEdges.length) {
    yield call(manageLayerElements, {
      payload: {
        layerId,
        body: layerEdges,
        withReq: true,
        subscribeBalances: false,
      },
    });
  }
  if (callback) callback(nodesMap);
}

/**
 * Массовое удаление акторов и связей
 */
function* bulkRemoveEls({ payload, callback }) {
  const { list, manageLayer } = payload;
  if (!manageLayer) {
    const { result } = yield call(api, {
      method: 'delete',
      url: '/actors_graph',
      body: list.map((i) => ({ id: i.actorId || i.edgeId, type: i.type })),
    });
    if (result !== RequestStatus.SUCCESS) return;
  }
  const activeLayer = yield getActiveLayer();
  const body = list.map((i) => ({
    action: 'delete',
    data: {
      id: i.actorId,
      model: i,
      type: i.type,
      withDuplicates: !manageLayer,
    },
  }));
  yield call(manageLayerElements, {
    payload: {
      layerId: activeLayer.id,
      activeElement: null,
      activeType: null,
      editableElement: null,
      body,
      withReq: manageLayer,
    },
  });
  if (callback) callback();
}

/**
 * Установить активный элемент графа
 */
function* setActiveElement({ payload }) {
  const { id, type } = payload;
  const copyNodes = yield call(getGraphEls, 'nodes');
  const copyEdges = yield call(getGraphEls, 'edges');
  const els = type === 'node' ? copyNodes : copyEdges;
  const findEl = els.find((i) => i.id === id);
  if (findEl) findEl.status = 'updated';
  yield sendReducerMsg({
    type: SET_ACTIVE_ELEMENT.SUCCESS,
    payload: {
      nodes: copyNodes,
      edges: copyEdges,
      activeElement: id,
      activeType: type,
      editableElement: null,
    },
  });
}

function* graphCommon() {
  yield takeEvery(SET_ACTIVE_ELEMENT.REQUEST, setActiveElement);
  yield takeEvery(BULK_REMOVE_FROM_GRAPH.REQUEST, bulkRemoveEls);
  yield takeEvery(BULK_ADD_TO_GRAPH.REQUEST, bulkAddEls);
  yield takeEvery(COPY_FROM_GRAPH.REQUEST, copyGraphElements);
  yield takeEvery(PASTE_TO_GRAPH.REQUEST, pasteGraphElements);
}

export default graphCommon;
